import { useEffect, useState } from 'react';
import { engineFetchJson } from '@/lib/engineHttp';
import type { WidgetDefinition, WidgetProps } from '@/types/widget';

interface ProviderCard {
  provider_id: string;
  display_name: string;
  status: string;
  has_stored_key: boolean;
  uses_env_key: boolean;
  configured: boolean;
  model_display_name: string | null;
  group_label: string;
}

interface ProviderGroup {
  id: string;
  label: string;
  providers: ProviderCard[];
}

interface ProviderDashboard {
  ready_count: number;
  not_configured_count: number;
  unavailable_count: number;
  groups: ProviderGroup[];
}

function statusDot(status: string): { color: string; label: string } {
  switch (status) {
    case 'ready':
      return { color: 'var(--sp-ok)', label: 'Ready' };
    case 'needs_key':
    case 'not_configured':
      return { color: 'var(--sp-warn)', label: 'Needs key' };
    case 'error':
    case 'unavailable':
      return { color: 'var(--sp-error)', label: 'Error' };
    default:
      return { color: 'var(--sp-text-dim)', label: status };
  }
}

function ProviderStatusWidget({ context }: WidgetProps) {
  const [data, setData] = useState<ProviderDashboard | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    void engineFetchJson<ProviderDashboard>('/settings/llm/providers')
      .then((d) => {
        setData(d);
        setError(null);
      })
      .catch((e) => {
        setError(e.message);
      });
  }, [context.authToken]);

  return (
    <div className="sp-widget">
      <div className="sp-widget-header">
        <span>Provider Status</span>
        <a href="/settings/models" style={{ fontSize: 11, fontWeight: 400, textTransform: 'none', letterSpacing: 0 }}>
          Configure
        </a>
      </div>
      <div className="sp-widget-body" style={{ padding: '4px 12px' }}>
        {error ? (
          <div>
            <div style={{ color: 'var(--sp-warn)', fontSize: 12, marginBottom: 4 }}>
              Awaiting engine endpoint
            </div>
            <div className="mono" style={{ color: 'var(--sp-text-dim)', fontSize: 11 }}>
              GET /api/v1/settings/llm/providers
            </div>
            {/* Mock layout showing what it will look like. Rows stay generic:
                no model names are bundled anywhere in the front end — every
                real row above comes from the engine read. */}
            <div style={{ marginTop: 12, opacity: 0.4 }}>
              {['Provider one', 'Provider two', 'Provider three'].map((name) => (
                <div key={name} style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '4px 0', fontSize: 12 }}>
                  <div style={{ width: 7, height: 7, borderRadius: '50%', background: 'var(--sp-text-dim)' }} />
                  <span style={{ color: 'var(--sp-text-muted)' }}>{name}</span>
                </div>
              ))}
            </div>
          </div>
        ) : data ? (
          <div>
            {/* Summary counters */}
            <div style={{ display: 'flex', gap: 16, marginBottom: 8, paddingBottom: 6, borderBottom: '1px solid var(--sp-border)' }}>
              <div>
                <span style={{ fontSize: 18, fontWeight: 700, color: 'var(--sp-ok)' }}>{data.ready_count}</span>
                <span style={{ fontSize: 11, color: 'var(--sp-text-dim)', marginLeft: 4 }}>ready</span>
              </div>
              <div>
                <span style={{ fontSize: 18, fontWeight: 700, color: 'var(--sp-warn)' }}>{data.not_configured_count}</span>
                <span style={{ fontSize: 11, color: 'var(--sp-text-dim)', marginLeft: 4 }}>need key</span>
              </div>
              {data.unavailable_count > 0 && (
                <div>
                  <span style={{ fontSize: 18, fontWeight: 700, color: 'var(--sp-error)' }}>{data.unavailable_count}</span>
                  <span style={{ fontSize: 11, color: 'var(--sp-text-dim)', marginLeft: 4 }}>error</span>
                </div>
              )}
            </div>

            {/* Provider list */}
            <div style={{ maxHeight: 200, overflow: 'auto' }}>
              {data.groups.map((group) =>
                group.providers.map((p) => {
                  const dot = statusDot(p.status);
                  return (
                    <div
                      key={p.provider_id}
                      style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '3px 0', fontSize: 12 }}
                    >
                      <div
                        style={{
                          width: 7,
                          height: 7,
                          borderRadius: '50%',
                          background: dot.color,
                          flexShrink: 0,
                        }}
                        title={dot.label}
                      />
                      <span style={{ flex: 1, color: 'var(--sp-text)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                        {p.display_name}
                      </span>
                      {p.model_display_name && (
                        <span className="mono" style={{ fontSize: 10, color: 'var(--sp-text-dim)' }}>
                          {p.model_display_name}
                        </span>
                      )}
                    </div>
                  );
                }),
              )}
            </div>
          </div>
        ) : (
          <div style={{ color: 'var(--sp-text-muted)', fontSize: 12 }}>Loading providers...</div>
        )}
      </div>
    </div>
  );
}

export const providerStatusWidget: WidgetDefinition = {
  id: 'provider-status',
  name: 'Provider Status',
  description: 'One-line status per enabled LLM provider.',
  icon: 'server',
  category: 'workspace',
  scope: 'both',
  defaultSize: { w: 6, h: 4 },
  minSize: { w: 3, h: 3 },
  component: ProviderStatusWidget,
};
